import EditableDataTable from '../components/EditableDataTable';
import React, { useState, useEffect } from 'react';
import * as api from '../services/api';

// ITT status workflow:
// awaiting_qualification -> classification_in_progress -> cleared -> (SharePoint upload)
// DB ENUM: awaiting_qualification | classification_in_progress | cleared | rejected

const STATUS_FLOW = {
  awaiting_qualification:     { label:'Awaiting Review',    cls:'badge-blue',  next:'classification_in_progress', nextLabel:'Accept & Begin Review' },
  classification_in_progress: { label:'Under Review',       cls:'badge-gold',  next:'cleared',                    nextLabel:'Clear & Approve for Bidding' },
  cleared:                    { label:'Cleared / Approved', cls:'badge-green', next:null,                         nextLabel:null },
  rejected:                   { label:'Rejected',           cls:'badge-red',   next:null,                         nextLabel:null },
};

const EMPTY_FORM = {
  title:'', issuing_authority:'', contract_value:'', deadline:'',
  risk_level:'medium', description:'', submitting_department:'',
};

export default function ITTQualification({ showToast, editMode, currentUser }) {
  const [records, setRecords]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showForm, setShowForm]       = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  const [form, setForm]               = useState(EMPTY_FORM);
  const [ittFile, setIttFile]         = useState(null);          // doc attached at registration
  const [ittDocs, setIttDocs]         = useState({});            // { [ittId]: [doc, ...] }
  const [selected, setSelected]       = useState(null);          // cleared ITT for SharePoint upload
  const [approvalFile, setApprovalFile] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);

  useEffect(() => { loadAll(); }, []);

  async function loadAll() {
    try {
      const { data } = await api.getITTRecords();
      const recs = data || [];
      setRecords(recs);
      // Load all ITT-linked documents in one call
      const docsRes = await api.getDocuments({ linked_entity_type: 'itt' });
      const docs = docsRes.data || [];
      const map = {};
      docs.forEach(d => {
        if (!map[d.linked_entity_id]) map[d.linked_entity_id] = [];
        map[d.linked_entity_id].push(d);
      });
      setIttDocs(map);
    } catch { showToast('Failed to load ITT records'); }
    finally { setLoading(false); }
  }

  async function submitITT() {
    if (!form.title.trim()) { showToast('Tender title is required'); return; }
    if (!ittFile) { showToast('Please attach the ITT document — Legal needs it to review'); return; }
    setSubmitting(true);
    try {
      // 1. Create the ITT record
      const { data: newITT } = await api.createITTRecord({
        title: form.title,
        issuing_authority: form.issuing_authority || null,
        contract_value: form.contract_value ? parseFloat(form.contract_value) : null,
        deadline: form.deadline || null,
        risk_level: form.risk_level,
        description: form.description || null,
        submitting_department: form.submitting_department || null,
      });

      // 2. Upload the attached document linked to this ITT
      const fd = new FormData();
      fd.append('file', ittFile);
      fd.append('linked_entity_type', 'itt');
      fd.append('linked_entity_id', newITT.id);
      fd.append('category', 'itt_document');
      fd.append('folder_path', 'ITT Register/' + newITT.ref + '/Submission');
      fd.append('description', 'ITT tender document submitted for legal review');
      await api.uploadDocument(fd);

      showToast('ITT registered and document uploaded successfully');
      setShowForm(false);
      setForm(EMPTY_FORM);
      setIttFile(null);
      loadAll();
    } catch (err) {
      showToast('Registration failed: ' + (err.response?.data?.error || err.message));
    } finally { setSubmitting(false); }
  }

  async function advanceStatus(record) {
    const nextStatus = STATUS_FLOW[record.status]?.next;
    if (!nextStatus) return;
    // Warn Legal if no document is attached and they are trying to accept
    if (record.status === 'awaiting_qualification' && !ittDocs[record.id]?.length) {
      if (!window.confirm('No document is attached to this ITT. Accept anyway?')) return;
    }
    try {
      await api.updateITTRecord(record.id, { status: nextStatus });
      if (nextStatus === 'cleared') {
        showToast('ITT cleared and approved for bidding. Upload the clearance document to SharePoint.');
        setSelected({ ...record, status: 'cleared' });
      } else {
        showToast('ITT status updated');
      }
      loadAll();
    } catch { showToast('Update failed'); }
  }

  async function rejectITT(id) {
    try { await api.updateITTRecord(id, { status:'rejected' }); showToast('ITT rejected'); loadAll(); }
    catch { showToast('Failed to reject ITT'); }
  }

  async function handleUploadApproval() {
    if (!approvalFile || !selected) { showToast('Select a file first'); return; }
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', approvalFile);
      fd.append('linked_entity_type', 'itt');
      fd.append('linked_entity_id', selected.id);
      fd.append('category', 'itt_approval');
      fd.append('folder_path', 'ITT Register/' + selected.ref + '/Approved');
      fd.append('description', 'Signed qualification clearance document');
      await api.uploadDocument(fd);
      showToast('Clearance document uploaded to SharePoint');
      setApprovalFile(null);
      setSelected(null);
      loadAll();
    } catch (err) {
      showToast('Upload failed: ' + (err.response?.data?.error || err.message));
    } finally { setUploadingDoc(false); }
  }

  const awaiting = records.filter(r => r.status === 'awaiting_qualification').length;
  const inReview = records.filter(r => r.status === 'classification_in_progress').length;
  const cleared  = records.filter(r => r.status === 'cleared').length;

  const categoryBadge = (cat) => {
    const cfg = {
      A: { cls:'badge-red',  label:'Category A — Major' },
      B: { cls:'badge-blue', label:'Category B — Significant' },
      C: { cls:'badge-gray', label:'Category C — Routine' },
    };
    const { cls, label } = cfg[cat] || { cls:'badge-gray', label: cat || 'Unclassified' };
    return <span className={'badge ' + cls}>{label}</span>;
  };

  const statusBadge = (s) => {
    const { cls, label } = STATUS_FLOW[s] || { cls:'badge-gray', label:(s||'').replace(/_/g,' ') };
    return <span className={'badge ' + cls}><span className="dot"></span>{label}</span>;
  };

  const riskBadge = (r) => (
    <span className={'badge ' + (r==='high'?'badge-red':r==='medium'?'badge-gold':'badge-gray')}>{r}</span>
  );

  return (
    <div>
      <div className="section-header">
        <div className="section-title">ITT Qualification & Classification</div>
        <button
          className="topbar-btn btn-ghost"
          onClick={async () => {
            try {
              const res = await api.exportITTRegister();
              const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
              const a = document.createElement('a');
              a.href = url;
              a.download = `ITT-Register-${new Date().toISOString().slice(0,10)}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            } catch (err) {
              let msg = err.message;
              if (err.response?.data instanceof Blob) {
                try { const t = await err.response.data.text(); msg = JSON.parse(t).error || t; } catch {}
              } else if (err.response?.data?.error) {
                msg = err.response.data.error;
              }
              alert('Export failed: ' + msg);
            }
          }}
        >⬇ Export ITT Register</button>
        <button className="topbar-btn btn-primary" onClick={() => setShowForm(!showForm)}>+ Register New ITT</button>
      </div>

      {/* KPI Cards */}
      <div className="row-3" style={{marginBottom:20}}>
        <div className="card" style={{textAlign:'center'}}>
          <div className="card-title" style={{justifyContent:'center'}}>Awaiting Review</div>
          <div style={{fontSize:28,fontWeight:700,color:'var(--mid)',fontFamily:'monospace'}}>{awaiting}</div>
          <div style={{fontSize:11,color:'var(--muted)',marginTop:4}}>Newly submitted ITTs</div>
        </div>
        <div className="card" style={{textAlign:'center'}}>
          <div className="card-title" style={{justifyContent:'center'}}>Under Review</div>
          <div style={{fontSize:28,fontWeight:700,color:'var(--warn)',fontFamily:'monospace'}}>{inReview}</div>
          <div style={{fontSize:11,color:'var(--muted)',marginTop:4}}>Being classified by Legal</div>
        </div>
        <div className="card" style={{textAlign:'center'}}>
          <div className="card-title" style={{justifyContent:'center'}}>Cleared & Approved</div>
          <div style={{fontSize:28,fontWeight:700,color:'var(--success)',fontFamily:'monospace'}}>{cleared}</div>
          <div style={{fontSize:11,color:'var(--muted)',marginTop:4}}>Approved for bidding</div>
        </div>
      </div>

      {/* Register Form */}
      {showForm && (
        <div className="card" style={{marginBottom:18}}>
          <div className="card-title">Register New ITT</div>
          <div className="form-grid">
            <div className="form-group full">
              <label>Tender Title <span style={{color:'var(--danger)'}}>*</span></label>
              <input type="text" value={form.title} onChange={e=>setForm({...form,title:e.target.value})} placeholder="Full title of the tender/ITT" />
            </div>
            <div className="form-group">
              <label>Issuing Authority</label>
              <input type="text" value={form.issuing_authority} onChange={e=>setForm({...form,issuing_authority:e.target.value})} placeholder="e.g. NLNG, Shell SPDC, NCDMB" />
            </div>
            <div className="form-group">
              <label>Submitting Department</label>
              <input type="text" value={form.submitting_department} onChange={e=>setForm({...form,submitting_department:e.target.value})} placeholder="e.g. Business Development" />
            </div>
            <div className="form-group">
              <label>Contract Value (NGN)</label>
              <input type="number" value={form.contract_value} onChange={e=>setForm({...form,contract_value:e.target.value})} placeholder="e.g. 200000000" />
            </div>
            <div className="form-group">
              <label>Submission Deadline</label>
              <input type="date" value={form.deadline} onChange={e=>setForm({...form,deadline:e.target.value})} />
            </div>
            <div className="form-group">
              <label>Risk Level</label>
              <select value={form.risk_level} onChange={e=>setForm({...form,risk_level:e.target.value})}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div className="form-group full">
              <label>Brief Description</label>
              <textarea value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="Scope of work, key requirements..." rows={2} style={{resize:'vertical'}} />
            </div>

            {/* Document Attachment — required for Legal to review */}
            <div className="form-group full" style={{background:'var(--light)',border:'2px dashed var(--border)',borderRadius:10,padding:14}}>
              <label style={{fontWeight:600,color:'var(--dark)'}}>
                📎 Attach ITT Document <span style={{color:'var(--danger)'}}>*</span>
                <span style={{fontWeight:400,color:'var(--muted)',marginLeft:6,fontSize:11}}>
                  Legal will review this document before accepting. (PDF, Word or Excel)
                </span>
              </label>
              <input
                type="file"
                accept=".pdf,.doc,.docx,.xlsx,.xls"
                style={{marginTop:8,fontSize:12}}
                onChange={e => setIttFile(e.target.files[0] || null)}
              />
              {ittFile && (
                <div style={{marginTop:6,fontSize:12,color:'var(--success)',fontWeight:600}}>
                  ✅ Ready to upload: {ittFile.name} ({(ittFile.size/1024).toFixed(0)} KB)
                </div>
              )}
              {!ittFile && (
                <div style={{marginTop:6,fontSize:11,color:'var(--danger)'}}>
                  A tender document must be attached — Legal cannot review without it.
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button className="topbar-btn btn-ghost" onClick={()=>{setShowForm(false);setIttFile(null);setForm(EMPTY_FORM);}}>Cancel</button>
            <button className="topbar-btn btn-primary" onClick={submitITT} disabled={submitting||!ittFile}>
              {submitting ? 'Registering & Uploading...' : 'Register ITT'}
            </button>
          </div>
        </div>
      )}

      {/* ITT Register Table */}
      <div className="card">
        <div className="card-title">ITT Register</div>
        {loading ? <div className="loading">Loading...</div> : (
          editMode ? (
            <EditableDataTable
              columns={[{key:'ref',label:'ITT Ref'},{key:'title',label:'Tender Title'},{key:'issuing_authority',label:'Issuing Authority'},{key:'contract_value',label:'Value'},{key:'deadline',label:'Deadline'},{key:'category',label:'Classification'},{key:'risk_level',label:'Risk'},{key:'status',label:'Status'}]}
              data={records}
              editable={true}
              onDataChange={updated=>setRecords(updated)}
              renderCell={(row,colKey) => {
                if (colKey==='ref') return <span style={{fontSize:11,fontWeight:600,color:'var(--mid)'}}>{row.ref}</span>;
                if (colKey==='contract_value') return row.contract_value ? 'NGN '+(row.contract_value/1e6).toFixed(0)+'M' : '-';
                if (colKey==='category') return row.category ? categoryBadge(row.category) : '-';
                if (colKey==='risk_level') return riskBadge(row.risk_level);
                if (colKey==='status') return statusBadge(row.status);
                if (colKey==='deadline') return row.deadline ? new Date(row.deadline).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '-';
                return row[colKey]??'-';
              }}
            />
          ) : (
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>ITT Ref</th><th>Tender Title</th><th>Issuing Authority</th>
                    <th>Value</th><th>Deadline</th><th>Category</th><th>Risk</th>
                    <th>Document</th><th>Status</th><th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {records.map(r => {
                    const docs = ittDocs[r.id] || [];
                    const submissionDoc = docs.find(d => d.category === 'itt_document' || d.category === 'itt_approval' || !d.category);
                    return (
                      <tr key={r.id}>
                        <td style={{fontWeight:600,color:'var(--mid)',fontSize:11}}>{r.ref}</td>
                        <td style={{maxWidth:180}}>{r.title}</td>
                        <td>{r.issuing_authority || '-'}</td>
                        <td style={{fontWeight:600}}>{r.contract_value ? 'NGN '+(r.contract_value/1e6).toFixed(0)+'M' : '-'}</td>
                        <td>{r.deadline ? new Date(r.deadline).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '-'}</td>
                        <td>{categoryBadge(r.category)}</td>
                        <td>{riskBadge(r.risk_level)}</td>
                        <td>
                          {docs.length > 0 ? (
                            <div style={{display:'flex',flexDirection:'column',gap:2}}>
                              {docs.map((d,i) => (
                                <a key={i}
                                  href={d.sharepoint_url || '#'}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  style={{fontSize:11,color:'var(--mid)',textDecoration:'none',display:'flex',alignItems:'center',gap:4}}
                                  title={d.description || d.original_filename}
                                >
                                  📄 {d.category === 'itt_approval' ? 'Clearance Doc' : 'ITT Document'}
                                </a>
                              ))}
                            </div>
                          ) : (
                            <span style={{fontSize:11,color:'var(--muted)'}}>No doc</span>
                          )}
                        </td>
                        <td>{statusBadge(r.status)}</td>
                        <td style={{whiteSpace:'nowrap'}}>
                          {STATUS_FLOW[r.status]?.next && (
                            <button className="sig-btn primary" style={{marginRight:4}} onClick={()=>advanceStatus(r)}>
                              {STATUS_FLOW[r.status].nextLabel}
                            </button>
                          )}
                          {r.status === 'cleared' && (
                            <button className="sig-btn ghost" style={{marginRight:4}} onClick={()=>setSelected(r)}>Upload Doc</button>
                          )}
                          {(r.status === 'awaiting_qualification' || r.status === 'classification_in_progress') && (
                            <button className="sig-btn ghost" style={{color:'var(--danger)'}} onClick={()=>rejectITT(r.id)}>Reject</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {records.length === 0 && (
                    <tr><td colSpan={10} style={{textAlign:'center',color:'var(--muted)',padding:24}}>No ITT records yet. Register the first ITT above.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {/* SharePoint Upload Panel — for cleared ITTs */}
      {selected && (
        <div className="card" style={{marginTop:18}}>
          <div className="card-title" style={{display:'flex',alignItems:'center',gap:10}}>
            <span>Upload Clearance Document — {selected.ref}</span>
            <span className="badge badge-green"><span className="dot"></span>Cleared</span>
            <button className="modal-close" style={{marginLeft:'auto'}} onClick={()=>{setSelected(null);setApprovalFile(null);}}>&times;</button>
          </div>
          <div style={{padding:'10px 0',fontSize:13,color:'var(--dark)'}}>
            Upload the signed qualification clearance letter for <strong>{selected.title}</strong>.
            It will be stored in the Legal SharePoint hub and accessible to all relevant stakeholders.
          </div>
          <div style={{display:'flex',alignItems:'center',gap:12,marginTop:4,flexWrap:'wrap'}}>
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={e=>setApprovalFile(e.target.files[0]||null)}
              style={{fontSize:12}}
            />
            {approvalFile && <span style={{fontSize:11,color:'var(--success)'}}>✅ {approvalFile.name}</span>}
            <button
              className="topbar-btn btn-primary"
              onClick={handleUploadApproval}
              disabled={!approvalFile||uploadingDoc}
              style={{marginLeft:'auto'}}
            >
              {uploadingDoc ? 'Uploading...' : 'Upload to SharePoint'}
            </button>
          </div>
          <div style={{marginTop:10,fontSize:11,color:'var(--muted)'}}>
            Destination: Legal SharePoint › ITT Register › {selected.ref} › Approved
          </div>
        </div>
      )}

      {/* Classification Framework */}
      <div className="card" style={{marginTop:18}}>
        <div className="card-title">ITT Classification Framework</div>
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:14,marginTop:6}}>
          <div style={{padding:14,background:'#FEF2F2',borderRadius:10,border:'1px solid #FECACA'}}>
            <div style={{fontWeight:700,color:'#991B1B',marginBottom:6}}>Category A — Major</div>
            <div style={{fontSize:12,color:'#7F1D1D'}}>Contract value above NGN 200M. Requires MD/Board approval, full legal review, financial due diligence, and JV/consortium assessment.</div>
          </div>
          <div style={{padding:14,background:'#FFFBEB',borderRadius:10,border:'1px solid #FDE68A'}}>
            <div style={{fontWeight:700,color:'#92400E',marginBottom:6}}>Category B — Significant</div>
            <div style={{fontSize:12,color:'#78350F'}}>NGN 50M to 200M. Requires ED approval, standard legal review, and compliance checks.</div>
          </div>
          <div style={{padding:14,background:'#ECFDF5',borderRadius:10,border:'1px solid #A7F3D0'}}>
            <div style={{fontWeight:700,color:'#065F46',marginBottom:6}}>Category C — Routine</div>
            <div style={{fontSize:12,color:'#064E3B'}}>Below NGN 50M. Department head approval, simplified legal checklist, standard PQQ review.</div>
          </div>
        </div>
      </div>
    </div>
  );
}
